/**
 * Session panel — the check-in's "record trail".
 *
 * A self-contained outlined card: a header carrying the panel's name and the
 * copy-intake-link action, a vertical chain of the records this check-in
 * produced (Customer -> Job -> Invoice), and a footer with provenance.
 *
 * The chain replaced a flat label/value list, which in turn replaced a row of
 * `info` chips: a chip carries a tone and reads as a *status*, while these are
 * references to other records, and the invoice one showed an opaque internal id
 * that looked like a number a customer could quote. The rail makes the ordering
 * legible — a customer is matched, then a job is raised, then an invoice is
 * opened — so a gap in the chain reads as "not there yet" rather than missing.
 *
 * The full intake URL is deliberately **not** displayed; it is long, it is
 * never read character by character, and the copy button is what staff use.
 * Express check-ins get no copy button at all: they are for established
 * business customers and have no customer intake step to send them to.
 *
 * The header is the collapse toggle — tap-anywhere, matching every other
 * section on the page — so the copy button stops its own click from reaching
 * it. Collapsed by default: this is reference material, not a step.
 *
 * Icons are Material (`@mui/icons-material`) rather than this app's usual
 * Tabler set, and the palette is the slate/brand-blue set given with the
 * design: #238bdd brand, #e2e8f0 / #cbd5e1 borders, #475569 / #64748b slate,
 * #f8fafc footer. Both are deliberate for this card — note they diverge from
 * the documented system tokens (accent #4A90D9, borders #e2eaf3), so don't
 * "correct" them to match other panels without checking.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Collapse } from '@mui/material';
import Business from '@mui/icons-material/Business';
import Check from '@mui/icons-material/Check';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import ContentCopy from '@mui/icons-material/ContentCopy';
import ExpandMore from '@mui/icons-material/ExpandMore';
import OpenInNew from '@mui/icons-material/OpenInNew';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import type { CheckInSession } from '@techgitter/contracts';

import { buildCheckInIntakeLink } from '../../../config/dataSourceConfig';
import {
  sessionCustomerRef,
  sessionInvoiceRef,
  sessionTicketRef,
} from './sessionDetailHelpers';

export interface CheckInSessionTokenCardProps {
  session: CheckInSession;
  /**
   * Whether the trail starts expanded. Collapsed by default, which is what the
   * Session panel did before it became this card — it is reference material,
   * not a step of the check-in.
   */
  defaultOpen?: boolean;
}

/** `06 Oct 26 03:45` — 24-hour en-AU, no comma between date and time. */
const fmtCreated = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Built from parts because en-AU renders `dd MMM yy, HH:mm` with a comma,
  // and `hourCycle: 'h23'` keeps midnight as 00 rather than 24.
  const parts = new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const at = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${at('day')} ${at('month')} ${at('year')} ${at('hour')}:${at('minute')}`;
};

interface TrailRow {
  key: string;
  label: string;
  icon: React.ReactNode;
  value: string;
  /** Where the value and the open-in-new button go — sm4rt when addressable. */
  href: string;
  /** The RepairShopr copy, only when it is a *different* target to `href`. */
  rsHref?: string;
  title?: string;
}

export const CheckInSessionTokenCard: React.FC<CheckInSessionTokenCardProps> = ({
  session,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const intakeLink = buildCheckInIntakeLink(session.token);
  const hasIntakeStep = session.intakeType !== 'express';

  // Clear a pending revert if the panel unmounts mid-countdown.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const toggle = () => setOpen(o => !o);

  const copyLink = async (e: React.MouseEvent) => {
    // The whole header toggles (tap-anywhere, like every other section), so
    // this button has to keep its click to itself.
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(intakeLink);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard denied — fall through silently */
    }
  };

  const customer = sessionCustomerRef(session);
  const ticket = sessionTicketRef(session);
  const invoice = sessionInvoiceRef(session);

  // Only records that exist get a node, so a young session shows a short chain
  // rather than empty rows. The RS link is carried only where it is a distinct
  // destination — pointing both affordances at the same URL says nothing.
  const rows: TrailRow[] = [];
  if (customer) {
    rows.push({
      key: 'customer',
      label: 'Customer',
      icon: <Business sx={{ fontSize: 16 }} />,
      value: customer.label,
      href: customer.href,
    });
  }
  if (ticket) {
    rows.push({
      key: 'job',
      label: 'Job',
      icon: <ConfirmationNumber sx={{ fontSize: 16 }} />,
      value: ticket.sm4rtHref ? `#${ticket.label}` : 'Open in RepairShopr',
      href: ticket.sm4rtHref ?? ticket.rsHref,
      rsHref: ticket.sm4rtHref ? ticket.rsHref : undefined,
      title: ticket.sm4rtHref ? undefined : `RepairShopr ticket id ${ticket.label}`,
    });
  }
  if (invoice) {
    rows.push({
      key: 'invoice',
      label: 'Invoice',
      icon: <ReceiptLong sx={{ fontSize: 16 }} />,
      value: invoice.label,
      href: invoice.sm4rtHref ?? invoice.rsHref,
      rsHref: invoice.sm4rtHref ? invoice.rsHref : undefined,
      title: invoice.title,
    });
  }

  return (
    <div className="ci-trail">
      <div
        className={`ci-trail__head${open ? ' ci-trail__head--open' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        /* Named explicitly: without it the toggle's accessible name absorbs the
           copy button's label and announces as "Session Intake link". */
        aria-label="Session"
        onClick={toggle}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="ci-trail__heading">Session</span>
        {hasIntakeStep && (
          <button
            type="button"
            className="ci-trail__copy"
            onClick={copyLink}
            title={copied ? 'Copied!' : 'Copy the public intake link'}
          >
            {copied ? (
              <Check sx={{ fontSize: 15 }} />
            ) : (
              <ContentCopy sx={{ fontSize: 15 }} />
            )}
            {copied ? 'Copied' : 'Intake link'}
          </button>
        )}
        <ExpandMore
          className={`ci-trail__chev${open ? ' ci-trail__chev--open' : ''}`}
          sx={{ fontSize: 18 }}
        />
      </div>

      <Collapse in={open} unmountOnExit>
      {rows.length > 0 && (
        <div className="ci-trail__body">
          {rows.map((row, idx) => (
            <div className="ci-trail__row" key={row.key}>
              <div className="ci-trail__rail">
                <span className="ci-trail__node">{row.icon}</span>
                {/* The connector joins this node to the next, so the last
                    rendered row never has one. */}
                {idx < rows.length - 1 && <span className="ci-trail__line" />}
              </div>

              <div className="ci-trail__main">
                <span className="ci-trail__label">{row.label}</span>
                <a
                  className="ci-trail__value"
                  href={row.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={row.title}
                >
                  {row.value}
                </a>
              </div>

              <div className="ci-trail__actions">
                {row.rsHref && (
                  <a
                    className="ci-trail__rs"
                    href={row.rsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.title ?? `Open the ${row.label.toLowerCase()} in RepairShopr`}
                  >
                    RS
                  </a>
                )}
                <a
                  className="ci-trail__open"
                  href={row.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open ${row.label.toLowerCase()}`}
                >
                  <OpenInNew sx={{ fontSize: 16 }} />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="ci-trail__foot">
        <span className="ci-trail__created">
          {fmtCreated(session.createdAt)} · {session.createdBy || '—'}
        </span>
        {session.integrationMessageId && (
          <span className="ci-trail__msg" title={session.integrationMessageId}>
            {session.integrationMessageId}
          </span>
        )}
      </div>
      </Collapse>
    </div>
  );
};
